import { logger } from "@nova/telemetry"
import {
    forwardRef,
    MouseEventHandler,
    MutableRefObject,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import { InfraredImageContext } from "./types"
import { useHtmlId } from "@nova/use-html-id"
import { useTranslation } from "@nova/use-translation"
import * as Icons from "@nova/component-icons"
import { InfraredImageWithContext } from "@nova/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectHotspotPanelItems } from "panels/hotspot/selectors"
import classNames from "classnames"
import { setHotspotPanelState } from "panels/hotspot/slice"
import {
    TransformWrapper,
    TransformComponent,
    ReactZoomPanPinchRef,
    ReactZoomPanPinchContentRef,
} from "react-zoom-pan-pinch"
import { debounce } from "nodash"
import { preload } from "swr"
import { useSWRNoRevalidate } from "@nova/swr"
import {
    retrieveInfraredImageCornersFromFlightId,
    retrieveInfraredImageFromStorageKey,
} from "./module"

const buildKey = (s3Key: string | undefined, isDemo: boolean = false) => {
    return `${s3Key}-${isDemo}`
}

export const prePopulateInfraredImageData = (s3Key: string, isDemo: boolean = false) => {
    preload(buildKey(s3Key, isDemo), () => retrieveInfraredImageFromStorageKey(s3Key, isDemo))
}

export const useInfraredImageData = (s3Key: string | undefined, isDemo?: boolean) => {
    const { data, error, isLoading } = useSWRNoRevalidate(buildKey(s3Key, !!isDemo), () => {
        if (!s3Key) return
        return retrieveInfraredImageFromStorageKey(s3Key, isDemo)
    })
    return { data, error, loading: isLoading }
}

export const useInfraredImageCorners = (
    flightId: string,
    { disabled }: { disabled?: boolean } = {}
) => {
    const { data, error, isLoading } = useSWRNoRevalidate(flightId, () =>
        disabled ? undefined : retrieveInfraredImageCornersFromFlightId(flightId)
    )

    return { data, isLoading, error }
}

const ImagramHeight = 80

type InfraredImageProps = {
    infraredImage: InfraredImageWithContext
    context: InfraredImageContext[] | undefined
    excludeY?: boolean
    zoomable?: boolean
    onZoomLevelChange?: (zoom: number) => void
    ref?: MutableRefObject<ReactZoomPanPinchContentRef | null>
    isDemo?: boolean
}

const DefaultInfraredImageDimensions = {
    width: 640,
    height: 512,
}

const maxNumberOfMsBeforeLoggingWarning = 3000

interface TransformedPoint {
    hotspotId: string
    x: number
    y: number
}

const horizontalCrosshairId = "horizontal-crosshair"
const verticalCrosshairId = "vertical-crosshair"
const yImagramId = "y-imagram"
const xImagramId = "x-imagram"

/**
 * displays a visible infrared image
 */
const InfraredImage = forwardRef<ReactZoomPanPinchContentRef, InfraredImageProps>(
    (
        {
            infraredImage,
            context,
            excludeY,
            zoomable = true,
            onZoomLevelChange,
            isDemo,
        }: InfraredImageProps,
        ref
    ) => {
        const { t } = useTranslation()
        const timeoutRef = useRef<NodeJS.Timeout>(undefined)
        const {
            data: infraredImageData,
            error: infraredImageError,
            loading: infraredImageLoading,
        } = useInfraredImageData(infraredImage.s3Key, isDemo)
        const selectedHotspotPanel = useAppSelector(selectHotspotPanelItems)
        const dispatch = useAppDispatch()
        const zoomRef = useRef<ReactZoomPanPinchContentRef>(null)

        useImperativeHandle<ReactZoomPanPinchContentRef | null, ReactZoomPanPinchContentRef | null>(
            ref,
            () => zoomRef.current,
            []
        )

        const [isHoveringImage, setIsHoveringImage] = useState(false)

        const onMouseMove = () => {
            const hor = document.getElementById(horizontalCrosshairId)
            const vert = document.getElementById(verticalCrosshairId)
            const yImagram = document.getElementById(yImagramId)
            return ((ev) => {
                if (!hor || !vert) return

                const rect = (ev.currentTarget as HTMLDivElement).getBoundingClientRect()
                const relX = ev.clientX - rect.left
                const relY = ev.clientY - rect.top

                const yImagramWidth = yImagram?.getBoundingClientRect().width ?? 0

                hor.style.transform = `translateY(${relY}px)`
                vert.style.transform = `translateX(${relX + yImagramWidth}px)`
            }) as MouseEventHandler
        }

        const onPan = () => {
            const xImagram = document.getElementById(xImagramId)
            const yImagram = document.getElementById(yImagramId)
            return (ref: ReactZoomPanPinchRef) => {
                if (!yImagram || !xImagram) return
                yImagram.style.transform = `scaleY(${ref.state.scale}) translateY(${ref.state.positionY / ref.state.scale}px)`
                yImagram.style.transformOrigin = "left top"

                // Update X-axis histogram (bottom SVG)
                xImagram.style.transform = `scaleX(${ref.state.scale}) translateX(${ref.state.positionX / ref.state.scale}px)`
                xImagram.style.transformOrigin = "left top"
            }
        }
        // okay this is really confusing and stupid but here's whats going on, the x,y coordinates were originally pixel values relative to the original image
        // the problem with that is tracking the x,y from preprocessed image to visible image to possibly transformed visible image was too complicated, bugs kept coming up
        // the solution was to normalize between 0-640 for x and 0-512 for y always, I wanted to do 0-1 but I can't store floats in protobuf when it was already defined to use ints
        // this code currently works for new hotspots and old hotspots, but obviously this solution is messy and complicated, be very careful when copying over
        const transformedPerimeters = useMemo<TransformedPoint[][] | undefined>(() => {
            return context?.map((c) => {
                return c.relativeImageCoordinates && !infraredImageData?.loading
                    ? c.perimeter.map(({ x, y }) => ({
                          hotspotId: c.id,
                          x: (x / 640) * (infraredImageData?.width ?? 0),
                          y: (y / 512) * (infraredImageData?.height ?? 0),
                      }))
                    : c.perimeter.map(({ x, y }) => ({ hotspotId: c.id, x, y }))
            })
        }, [context, infraredImageData])

        useEffect(() => {
            // this should only run once on first load, assuming the prop does not change several times:
            timeoutRef.current = setTimeout(() => {
                logger.warn("infrared image load delay", { infraredImageId: infraredImage.id })
            }, maxNumberOfMsBeforeLoggingWarning)

            return () => {
                clearTimeout(timeoutRef.current)
            }
        }, [infraredImage])

        const graphData = useMemo(() => {
            let d = ""
            if (!infraredImageLoading && !infraredImageError && infraredImageData?.imagram) {
                const img = infraredImageData.imagram.map(transformImagram)
                d += `M0 ${ImagramHeight}`
                for (let i = 0; i < img.length; i++) {
                    const x1 =
                        ((DefaultInfraredImageDimensions.width - ImagramHeight) * i) / img.length
                    const x2 =
                        ((DefaultInfraredImageDimensions.width - ImagramHeight) * (i + 1)) /
                        img.length
                    const y = ImagramHeight - img[i] * ImagramHeight
                    d += `L${x1} ${y}L${x2} ${y}`
                }
                d += `L${DefaultInfraredImageDimensions.width - ImagramHeight} ${ImagramHeight}Z`
            }
            return d
        }, [infraredImageData?.imagram, infraredImageError, infraredImageLoading])

        // Your existing graphDataY function (left histogram)
        const graphDataY = useMemo(() => {
            let d = ""
            if (!infraredImageLoading && !infraredImageError && infraredImageData?.imagramY) {
                const img = infraredImageData.imagramY.map(transformImagram)
                d += `M0 ${ImagramHeight}`
                for (let i = 0; i < img.length; i++) {
                    const x1 =
                        ((DefaultInfraredImageDimensions.height - ImagramHeight) * i) / img.length
                    const x2 =
                        ((DefaultInfraredImageDimensions.height - ImagramHeight) * (i + 1)) /
                        img.length
                    const y = ImagramHeight - img[i] * ImagramHeight
                    d += `L${x1} ${y}L${x2} ${y}`
                }
                d += `L${DefaultInfraredImageDimensions.height - ImagramHeight} ${ImagramHeight}Z`
            }
            return d
        }, [infraredImageData?.imagramY, infraredImageError, infraredImageLoading])
        const imagramId = useHtmlId()

        const imageUrl = infraredImageData?.imageUrl
        // keep the image aspect ratio, but we need it to be the width we have configured for our viewport
        const viewportHeight = Math.ceil(
            DefaultInfraredImageDimensions.width *
                ((infraredImageData?.height || 0) / (infraredImageData?.width || 1))
        )
        const xMult = (infraredImageData && 640 / infraredImageData.width) || 1
        const yMult = (infraredImageData && viewportHeight / infraredImageData.height) || 1
        const hotspots = (() => {
            return transformedPerimeters?.map((transformedPerimeter) => {
                if (!transformedPerimeter) return {}
                const xs = transformedPerimeter.map((p) => p.x),
                    ys = transformedPerimeter.map((p) => p.y),
                    minX = Math.min(...xs),
                    maxX = Math.max(...xs),
                    minY = Math.min(...ys),
                    maxY = Math.max(...ys),
                    width = maxX - minX,
                    height = maxY - minY,
                    hotspotId = transformedPerimeter.at(0)?.hotspotId
                if (width > 10 && height > 10) {
                    // box
                    return {
                        hotspotId,
                        hotspotX: minX * xMult,
                        hotspotY: minY * yMult,
                        hotspotW: width * xMult,
                        hotspotH: height * yMult,
                    }
                } else {
                    return {
                        hotspotId,
                        hotspotX: ((maxX + minX) / 2 - 5) * xMult,
                        hotspotY: ((maxY + minY) / 2 - 5) * yMult,
                        hotspotW: 10,
                        hotspotH: 10,
                    }
                }
            })
        })()

        if (infraredImageLoading) {
            return (
                <div
                    className="flex flex-col items-center justify-center w-full h-[451px"
                    style={{
                        aspectRatio: `${DefaultInfraredImageDimensions.width} / ${DefaultInfraredImageDimensions.height}`,
                    }}
                >
                    <div className="loader" />
                </div>
            )
        } else if (infraredImageError) {
            return (
                <div className="flex justify-center items-center h-[451px]">
                    <Icons.ErrorExclamation className={"!w-6 !h-5 !mr-1"} />
                    <div>{t("Error displaying image")}</div>
                </div>
            )
        }
        return (
            <TransformWrapper
                onTransformed={onPan()}
                wheel={{
                    /* I don't know why, but this makes the trackpad pinch to zoom actually work, with no other side effects I can find. */
                    disabled: !zoomable,
                    smoothStep: 0.01,
                }}
                panning={{ disabled: !zoomable }}
                onZoom={debounce((zoom) => onZoomLevelChange?.(zoom.state.scale), 50)}
                ref={zoomRef}
            >
                <div
                    className="grid relative"
                    style={{
                        gridTemplateColumns: excludeY ? "0fr 1fr" : "0.1fr 0.9fr", // Use 1fr so column scales with container
                        gridTemplateRows: "0.9fr 10%", // Use 1fr for first row too
                        aspectRatio: `${DefaultInfraredImageDimensions.width} / ${DefaultInfraredImageDimensions.height}`,
                    }}
                >
                    <div
                        className={classNames(
                            "absolute block w-full h-[1px] bg-transparent z-50 pointer-events-none",
                            isHoveringImage && "bg-white-800"
                        )}
                        id={horizontalCrosshairId}
                    />

                    <div
                        className={classNames(
                            "absolute block h-full w-[1px] bg-transparent z-50 pointer-events-none",
                            isHoveringImage && "bg-white-800"
                        )}
                        id={verticalCrosshairId}
                    />
                    <div
                        className="h-full w-full inline-block cursor-none"
                        style={{
                            gridArea: "1 / 2 / 2 / 3",
                        }}
                        onMouseEnter={(e) => {
                            setIsHoveringImage(true)
                            onMouseMove()(e)
                        }}
                        onMouseLeave={() => setIsHoveringImage(false)}
                        onMouseMove={onMouseMove()}
                    >
                        <div className="relative">
                            <TransformComponent
                                wrapperClass="!w-full !h-full"
                                contentClass="!w-full !h-full"
                            >
                                <img
                                    src={imageUrl}
                                    alt="Infrared image"
                                    className="w-full h-full"
                                />
                                <div>
                                    {hotspots?.map(
                                        ({ hotspotH, hotspotW, hotspotX, hotspotY, hotspotId }) =>
                                            !!hotspotH &&
                                            !!hotspotW &&
                                            !!hotspotX &&
                                            !!hotspotY && (
                                                <div
                                                    key={hotspotId}
                                                    className={classNames(
                                                        "absolute border-[1px] hover:border-[2px] border-white-700 hover:border-white-1000 cursor-pointer",
                                                        hotspotId ===
                                                            selectedHotspotPanel.hotspotId &&
                                                            "!border-red-500 border-[2px]"
                                                    )}
                                                    onClick={() =>
                                                        dispatch(
                                                            setHotspotPanelState({
                                                                hotspotId,
                                                            })
                                                        )
                                                    }
                                                    style={{
                                                        top: `${(hotspotY / DefaultInfraredImageDimensions.height) * 100}%`,
                                                        left: `${(hotspotX / DefaultInfraredImageDimensions.width) * 100}%`,
                                                        height: `max(${(hotspotH / DefaultInfraredImageDimensions.height) * 100}%, 3%)`,
                                                        width: `max(${(hotspotW / DefaultInfraredImageDimensions.width) * 100}%, 3%)`,
                                                        transform: "translate(-25%, -25%)",
                                                    }}
                                                ></div>
                                            )
                                    )}
                                </div>
                            </TransformComponent>
                        </div>
                    </div>

                    {!excludeY && (
                        <div className="overflow-hidden">
                            <svg
                                style={{ gridArea: "1 / 1 / 2 / 2" }}
                                className="w-full h-full"
                                viewBox={`0 0 ${ImagramHeight} ${DefaultInfraredImageDimensions.height - ImagramHeight}`}
                                preserveAspectRatio="none"
                                id={yImagramId}
                            >
                                <defs>
                                    <linearGradient id={imagramId} x1={0} y1={0} x2={0} y2={1}>
                                        <stop offset="6.25%" stopColor="#027BFF" />
                                        <stop offset="57.82%" stopColor="#005BBE" />
                                        <stop offset="96.36%" stopColor="#003978" />
                                    </linearGradient>
                                </defs>
                                <path
                                    transform={`rotate(90) translate(0, -${ImagramHeight})`}
                                    d={graphDataY}
                                    fill={`url(#${imagramId})`}
                                />
                            </svg>
                        </div>
                    )}

                    <div className="overflow-hidden" style={{ gridArea: "2 / 2 / 3 / 3" }}>
                        <svg
                            className="w-full h-full"
                            viewBox={`0 0 ${DefaultInfraredImageDimensions.width - ImagramHeight} ${ImagramHeight}`}
                            preserveAspectRatio="none"
                            id={xImagramId}
                        >
                            <defs>
                                <linearGradient id={imagramId} x1={0} y1={0} x2={0} y2={1}>
                                    <stop offset="6.25%" stopColor="#027BFF" />
                                    <stop offset="57.82%" stopColor="#005BBE" />
                                    <stop offset="96.36%" stopColor="#003978" />
                                </linearGradient>
                            </defs>
                            <path d={graphData} fill={`url(#${imagramId})`} />
                        </svg>
                    </div>

                    {!excludeY && (
                        <svg
                            style={{
                                gridArea: "2 / 1 / 2 / 1",
                                transform: `rotate(${(infraredImage.yaw ?? 0) * -1}deg)`,
                            }}
                            className="m-auto border border-gray-300 rounded-full p-[5%] aspect-square w-[45%] h-[55%]"
                            viewBox={`0 0 24 24`}
                        >
                            <Icons.CompassNeedleLight />
                        </svg>
                    )}
                </div>
            </TransformWrapper>
        )
    }
)

/**
 * Sensortool returns imagrams as a float32[] representing the heat of the imagram, we want to scale it
 * between 0 and 12 inclusive, and then / 12 to get a value between 0-1 which the SVG can display.
 * @param number a single value from a sensortool generated imagram.
 * @returns a scaled version of the same number
 */
const transformImagram = (number: number) => {
    return Math.max(Math.min(12, number), 0) / 12
}

InfraredImage.displayName = "InfraredImage"
export { InfraredImage }
